import { useState, useEffect, useRef } from "react";

const PRICE_PER_IMAGE = 15;
const PRICE_PER_VIDEO = 25;
const FILM_BASE_PRICE = 625;
const PRICE_PER_FILM_SECOND = 25;
const MIN_FILM_SECONDS = 20;
const MONTHLY_DISCOUNT = 0.20;
const FORMAT_EXTRA_PRICE = 350;
const MINIMUM_ORDER = 990;
const IMAGE_MIN = 20; const IMAGE_MAX = 160; const IMAGE_STEP = 10;
const VIDEO_MIN = 10; const VIDEO_MAX = 60; const VIDEO_STEP = 5;
const FORMSPREE_ENDPOINT = "https://formspree.io/f/xwvjydzr";
const HEAR_OPTIONS = ["Google", "Instagram", "LinkedIn", "Referral", "Other"];

const ALL_FORMATS = [
  { id: "ig_post",   label: "Instagram Post",         spec: "1080 × 1350" },
  { id: "ig_story",  label: "Instagram Story / Reel", spec: "1080 × 1920" },
  { id: "landscape", label: "Landscape",              spec: "16 : 9" },
  { id: "banner",    label: "Display Banner",         spec: "1200 × 628" },
  { id: "linkedin",  label: "LinkedIn",               spec: "1200 × 627" },
];

function usePrevious(value) {
  const ref = useRef();
  useEffect(() => { ref.current = value; });
  return ref.current;
}

function AnimatedPrice({ value }) {
  const [display, setDisplay] = useState(value);
  const prev = usePrevious(value);
  useEffect(() => {
    if (prev === undefined || prev === value) { setDisplay(value); return; }
    const diff = value - prev;
    const duration = 350;
    const t0 = performance.now();
    const step = (now) => {
      const t = Math.min((now - t0) / duration, 1);
      const e = t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
      setDisplay(Math.round(prev + diff * e));
      if (t < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [value]);
  return <span>${display.toLocaleString()}</span>;
}

// Section row — label left, toggle button right
function SectionRow({ label, enabled, onEnable, onRemove, addLabel }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "20px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <div style={{ width: "5px", height: "5px", borderRadius: "50%", background: enabled ? "#fff" : "#333", transition: "background 0.2s" }} />
        <span style={{ fontSize: "11px", letterSpacing: "0.14em", textTransform: "uppercase", color: enabled ? "#ccc" : "#555", fontWeight: 600 }}>{label}</span>
      </div>
      <button
        onClick={() => enabled ? onRemove() : onEnable()}
        style={{
          padding: "6px 16px", borderRadius: "100px",
          border: `1px solid ${enabled ? "#555" : "#252525"}`,
          background: enabled ? "#1e1e1e" : "transparent",
          color: enabled ? "#fff" : "#555",
          fontSize: "11px", letterSpacing: "0.06em", textTransform: "uppercase",
          cursor: "pointer", fontFamily: "inherit", transition: "all 0.2s", fontWeight: 500
        }}
      >{enabled ? "Remove" : addLabel}</button>
    </div>
  );
}

function RangeSlider({ value, min, max, step, onChange, unit = "" }) {
  const pct = ((value - min) / (max - min)) * 100;
  return (
    <div style={{ marginBottom: "6px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: "10px" }}>
        <span style={{ fontSize: "11px", letterSpacing: "0.1em", textTransform: "uppercase", color: "#555" }}>Quantity</span>
        <span style={{ fontFamily: "'DM Mono', monospace", fontSize: "20px", color: "#fff", fontWeight: 500 }}>{value}{unit}</span>
      </div>
      <div style={{ position: "relative", height: "32px", display: "flex", alignItems: "center" }}>
        <div style={{ position: "absolute", width: "100%", height: "3px", background: `linear-gradient(to right, #fff ${pct}%, #252525 ${pct}%)`, borderRadius: "2px" }} />
        <input type="range" min={min} max={max} step={step} value={value}
          onChange={e => onChange(Number(e.target.value))}
          style={{ position: "relative", width: "100%", appearance: "none", background: "transparent", cursor: "pointer", height: "32px", zIndex: 1 }} />
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: "2px" }}>
        <span style={{ fontFamily: "'DM Mono', monospace", fontSize: "10px", color: "#333" }}>{min}{unit}</span>
        <span style={{ fontFamily: "'DM Mono', monospace", fontSize: "10px", color: "#333" }}>{max}{unit}</span>
      </div>
    </div>
  );
}

function CostLine({ children }) {
  return <div style={{ fontFamily: "'DM Mono', monospace", fontSize: "11px", color: "#555", marginTop: "8px" }}>{children}</div>;
}

function InputField({ label, required, error, children }) {
  return (
    <div style={{ marginBottom: "12px" }}>
      <div style={{ fontSize: "10px", letterSpacing: "0.12em", textTransform: "uppercase", color: error ? "#c84040" : "#555", marginBottom: "6px", fontWeight: 600 }}>
        {label}{required && <span style={{ color: "#444", marginLeft: "3px" }}>*</span>}
      </div>
      {children}
      {error && <div style={{ fontSize: "11px", color: "#c84040", marginTop: "4px" }}>Required</div>}
    </div>
  );
}

const iStyle = { width: "100%", padding: "10px 14px", background: "#111", border: "1px solid #1e1e1e", borderRadius: "8px", color: "#fff", fontSize: "13px", fontFamily: "inherit", outline: "none", boxSizing: "border-box", transition: "border-color 0.2s" };

export default function PricingCalculator() {
  const [imagesEnabled, setImagesEnabled] = useState(false);
  const [images, setImages] = useState(IMAGE_MIN);
  const [videosEnabled, setVideosEnabled] = useState(false);
  const [videos, setVideos] = useState(VIDEO_MIN);
  const [filmEnabled, setFilmEnabled] = useState(false);
  const [films, setFilms] = useState([{ id: 1, seconds: MIN_FILM_SECONDS }]);
  const addFilm = () => setFilms(prev => [...prev, { id: Date.now(), seconds: MIN_FILM_SECONDS }]);
  const removeFilm = (id) => setFilms(prev => prev.filter(f => f.id !== id));
  const updateFilm = (id, seconds) => setFilms(prev => prev.map(f => f.id === id ? { ...f, seconds } : f));
  const [monthly, setMonthly] = useState(false);
  const [selectedFormats, setSelectedFormats] = useState(["ig_post"]);
  const [showForm, setShowForm] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [form, setForm] = useState({ firstName: "", lastName: "", company: "", email: "", phone: "", website: "", objective: "", additionalInfo: "", hearFrom: "" });
  const [errors, setErrors] = useState({});
  const formRef = useRef(null);

  const toggleFormat = (id) => setSelectedFormats(prev =>
    prev.includes(id) ? (prev.length === 1 ? prev : prev.filter(x => x !== id)) : [...prev, id]
  );

  const extraFormats = Math.max(0, selectedFormats.length - 1);
  const imageCost  = imagesEnabled ? images * PRICE_PER_IMAGE : 0;
  const videoCost  = videosEnabled ? videos * PRICE_PER_VIDEO : 0;
  const filmCost   = filmEnabled ? films.reduce((acc, f) => acc + FILM_BASE_PRICE + Math.max(0, f.seconds - MIN_FILM_SECONDS) * PRICE_PER_FILM_SECOND, 0) : 0;
  const filmCount  = filmEnabled ? films.length : 0;
  // First format always free. Extra formats × $350 × max(1, filmCount)
  const formatCost = extraFormats * FORMAT_EXTRA_PRICE * Math.max(1, filmCount);
  const subtotal   = imageCost + videoCost + filmCost + formatCost;
  const nothingSelected = !imagesEnabled && !videosEnabled && !filmEnabled;
  const belowMinimum    = !nothingSelected && subtotal < MINIMUM_ORDER;
  const total = monthly ? Math.round(subtotal * (1 - MONTHLY_DISCOUNT)) : subtotal;
  const canProceed = !nothingSelected && !belowMinimum;
  const delivery = filmEnabled ? "12–18 days" : imagesEnabled && images > 60 ? "8–14 days" : "5–10 days";
  const tier = total < 1500 ? "Editorial Drop" : total < 3000 ? "Campaign Pack" : "Full Production";

  const buildQuoteSummary = () => [
    imagesEnabled && `${images} editorial images × $${PRICE_PER_IMAGE} = $${imageCost}`,
    videosEnabled && `${videos} short loop videos × $${PRICE_PER_VIDEO} = $${videoCost}`,
    filmEnabled && films.map((f, i) => {
      const c = FILM_BASE_PRICE + Math.max(0, f.seconds - MIN_FILM_SECONDS) * PRICE_PER_FILM_SECOND;
      return `Hero film ${i + 1} (${f.seconds}s) = $${c}`;
    }).join("\n"),
    extraFormats > 0 && `${extraFormats} extra format(s) × $${FORMAT_EXTRA_PRICE} = $${formatCost}`,
    `Output formats: ${selectedFormats.join(", ")}`,
    `Monthly plan: ${monthly ? "Yes (20% off)" : "No"}`,
    `TOTAL: $${total.toLocaleString()}`,
  ].filter(Boolean).join("\n");

  useEffect(() => {
    if (showForm && formRef.current) setTimeout(() => formRef.current.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  }, [showForm]);

  const setField = (k, v) => { setForm(f => ({ ...f, [k]: v })); setErrors(e => ({ ...e, [k]: undefined })); };

  const validate = () => {
    const e = {};
    if (!form.firstName.trim()) e.firstName = true;
    if (!form.lastName.trim()) e.lastName = true;
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) e.email = true;
    if (!form.objective.trim()) e.objective = true;
    if (!form.hearFrom) e.hearFrom = true;
    return e;
  };

  const handleSubmit = async () => {
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSubmitting(true); setSubmitError("");
    try {
      const fd = new FormData();
      Object.entries({
        "First Name": form.firstName, "Last Name": form.lastName,
        "Company Name": form.company, "email": form.email,
        "Phone Number": form.phone, "Website": form.website,
        "Campaign Objective": form.objective, "Additional Information": form.additionalInfo,
        "Where did you hear about us?": form.hearFrom,
        "Quote Summary": buildQuoteSummary(), "Total Price": `$${total.toLocaleString()}`,
        "_subject": `New AVL Order — $${total.toLocaleString()} — ${form.firstName} ${form.lastName}`
      }).forEach(([k, v]) => fd.append(k, v));
      const res = await fetch(FORMSPREE_ENDPOINT, { method: "POST", headers: { Accept: "application/json" }, body: fd });
      if (res.ok) { setSubmitted(true); } else { throw new Error(); }
    } catch { setSubmitted(true); }
    finally { setSubmitting(false); }
    setTimeout(() => { window.location.href = "https://parallel-studio.co"; }, 2000);
  };

  const DIVIDER = <div style={{ height: "1px", background: "#1a1a1a", margin: "28px 0" }} />;

  // ── THANK YOU ──
  if (submitted) return (
    <div style={{ minHeight: "100vh", background: "#080808", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'DM Sans', sans-serif", padding: "40px 24px" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800&family=DM+Mono:wght@400;500&display=swap');*{box-sizing:border-box;margin:0;padding:0}`}</style>
      <div style={{ textAlign: "center", maxWidth: "400px" }}>
        <div style={{ width: "52px", height: "52px", borderRadius: "50%", border: "1px solid #252525", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 28px", fontSize: "20px", color: "#fff" }}>✓</div>
        <h1 style={{ fontSize: "36px", fontWeight: 800, color: "#fff", letterSpacing: "-0.02em", lineHeight: 1.1, marginBottom: "14px" }}>Request received.</h1>
        <p style={{ fontSize: "14px", color: "#666", lineHeight: 1.7, marginBottom: "36px" }}>
          Thank you, <strong style={{ color: "#ccc" }}>{form.firstName}</strong>. Someone from our team will get back to you within <strong style={{ color: "#ccc" }}>2 business days</strong>.
        </p>
        <p style={{ fontSize: "12px", color: "#444" }}>Redirecting you back to Parallel Studio…</p>
        <div style={{ padding: "24px 28px", border: "1px solid #1a1a1a", borderRadius: "14px", background: "#111", textAlign: "left" }}>
          <div style={{ fontSize: "10px", letterSpacing: "0.14em", textTransform: "uppercase", color: "#444", marginBottom: "10px" }}>Order summary</div>
          <div style={{ fontSize: "34px", fontWeight: 800, color: "#fff", letterSpacing: "-0.02em", marginBottom: "14px" }}>${total.toLocaleString()}</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
            {imagesEnabled && <span style={{ fontSize: "11px", color: "#777", border: "1px solid #1e1e1e", borderRadius: "100px", padding: "4px 12px" }}>{images} images</span>}
            {videosEnabled && <span style={{ fontSize: "11px", color: "#777", border: "1px solid #1e1e1e", borderRadius: "100px", padding: "4px 12px" }}>{videos} videos</span>}
            {filmEnabled && films.map((f, i) => (
              <span key={f.id} style={{ fontSize: "11px", color: "#777", border: "1px solid #1e1e1e", borderRadius: "100px", padding: "4px 12px" }}>Film {i + 1} ({f.seconds}s)</span>
            ))}
            <span style={{ fontSize: "11px", color: "#777", border: "1px solid #1e1e1e", borderRadius: "100px", padding: "4px 12px" }}>{selectedFormats.length} format{selectedFormats.length > 1 ? "s" : ""}</span>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", background: "#080808", fontFamily: "'DM Sans', sans-serif", color: "#fff" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800&family=DM+Mono:wght@400;500&display=swap');
        *{box-sizing:border-box;margin:0;padding:0}
        input[type=range]{-webkit-appearance:none;appearance:none;width:100%;height:3px;border-radius:2px;outline:none;cursor:pointer}
        input[type=range]::-webkit-slider-thumb{-webkit-appearance:none;width:20px;height:20px;border-radius:50%;background:#fff;border:2px solid #080808;box-shadow:0 0 0 2px #444;cursor:pointer;transition:transform 0.15s}
        input[type=range]::-webkit-slider-thumb:hover{transform:scale(1.15)}
        input[type=range]::-moz-range-thumb{width:20px;height:20px;border-radius:50%;background:#fff;border:2px solid #080808;cursor:pointer}
        input[type=text],input[type=email],input[type=tel],input[type=url],textarea{background:#111;border:1px solid #1e1e1e;border-radius:8px;color:#fff;font-size:13px;font-family:inherit;outline:none;width:100%;padding:10px 14px;box-sizing:border-box;transition:border-color 0.2s}
        input:focus,textarea:focus{border-color:#333}
        input::placeholder,textarea::placeholder{color:#555;font-size:11px}
        button:focus{outline:none}
        .fmt-pill:hover{border-color:#444!important}
        .hear-pill:hover{border-color:#444!important;color:#ccc!important}
        .section-card{transition:border-color 0.2s,background 0.2s}
      `}</style>

      {/* Header */}
      <div style={{ padding: "20px 32px", borderBottom: "1px solid #111", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: "4px" }}>
          <span style={{ fontWeight: 800, fontSize: "14px", letterSpacing: "0.02em" }}>AI VISUAL</span>
          <span style={{ fontWeight: 300, fontSize: "14px", letterSpacing: "0.12em", color: "#fff" }}>LAB</span>
        </div>
        <span style={{ fontSize: "12px", color: "#fff", fontWeight: 500 }}>Custom Production</span>
      </div>

      <div style={{ maxWidth: "560px", margin: "0 auto", padding: "52px 28px 100px" }}>

        {/* Title */}
        <div style={{ marginBottom: "48px" }}>
          <p style={{ fontSize: "11px", letterSpacing: "0.14em", textTransform: "uppercase", color: "#444", marginBottom: "10px", fontWeight: 600 }}>+ Productised AI Content</p>
          <h1 style={{ fontSize: "clamp(32px, 7vw, 48px)", fontWeight: 800, lineHeight: 1.05, letterSpacing: "-0.03em", marginBottom: "12px" }}>
            Build your<br />content package.
          </h1>
          <p style={{ fontSize: "14px", color: "#666", lineHeight: 1.65, maxWidth: "380px" }}>
            Select only what you need. Every deliverable is priced individually — no bundles, no compromises.
          </p>
        </div>

        {/* Monthly toggle */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", border: "1px solid #1a1a1a", borderRadius: "12px", marginBottom: "40px", background: monthly ? "#0e0e0e" : "transparent", transition: "background 0.3s" }}>
          <div>
            <div style={{ fontSize: "14px", color: "#ddd", marginBottom: "2px", fontWeight: 500 }}>Monthly production plan</div>
            <div style={{ fontSize: "12px", color: "#555" }}>Save 20% with recurring commitment</div>
          </div>
          <button onClick={() => setMonthly(!monthly)} style={{ width: "46px", height: "26px", borderRadius: "100px", background: monthly ? "#fff" : "#1e1e1e", border: "1px solid #2a2a2a", cursor: "pointer", position: "relative", transition: "background 0.25s", flexShrink: 0, marginLeft: "16px" }}>
            <div style={{ position: "absolute", top: "3px", left: monthly ? "22px" : "3px", width: "18px", height: "18px", borderRadius: "50%", background: monthly ? "#000" : "#444", transition: "left 0.25s, background 0.25s" }} />
          </button>
        </div>

        {/* ── IMAGES ── */}
        <div className="section-card" style={{ padding: "22px", border: `1px solid ${imagesEnabled ? "#252525" : "#141414"}`, borderRadius: "14px", background: imagesEnabled ? "#0e0e0e" : "transparent", marginBottom: "10px", transition: "all 0.2s" }}>
          <SectionRow label="Editorial Images" enabled={imagesEnabled}
            onEnable={() => setImagesEnabled(true)}
            onRemove={() => { setImagesEnabled(false); setImages(IMAGE_MIN); }}
            addLabel="+ Add images" />
          {!imagesEnabled
            ? <p style={{ fontSize: "13px", color: "#444", lineHeight: 1.6 }}>Art-directed AI editorial images. Min. 20 — $15 per image.</p>
            : <div>
                <RangeSlider value={images} min={IMAGE_MIN} max={IMAGE_MAX} step={IMAGE_STEP} onChange={setImages} />
                <CostLine>{images} images × ${PRICE_PER_IMAGE} = <strong style={{ color: "#aaa" }}>${imageCost.toLocaleString()}</strong></CostLine>
              </div>
          }
        </div>

        {/* ── VIDEOS ── */}
        <div className="section-card" style={{ padding: "22px", border: `1px solid ${videosEnabled ? "#252525" : "#141414"}`, borderRadius: "14px", background: videosEnabled ? "#0e0e0e" : "transparent", marginBottom: "10px", transition: "all 0.2s" }}>
          <SectionRow label="Short Loop Videos" enabled={videosEnabled}
            onEnable={() => setVideosEnabled(true)}
            onRemove={() => { setVideosEnabled(false); setVideos(VIDEO_MIN); }}
            addLabel="+ Add videos" />
          {!videosEnabled
            ? <p style={{ fontSize: "13px", color: "#444", lineHeight: 1.6 }}>Short loop videos 5–8s. Min. 10 — $25 per video.</p>
            : <div>
                <RangeSlider value={videos} min={VIDEO_MIN} max={VIDEO_MAX} step={VIDEO_STEP} onChange={setVideos} />
                <CostLine>{videos} videos × ${PRICE_PER_VIDEO} = <strong style={{ color: "#aaa" }}>${videoCost.toLocaleString()}</strong></CostLine>
              </div>
          }
        </div>

        {/* ── FILM ── */}
        <div className="section-card" style={{ padding: "22px", border: `1px solid ${filmEnabled ? "#252525" : "#141414"}`, borderRadius: "14px", background: filmEnabled ? "#0e0e0e" : "transparent", marginBottom: "32px", transition: "all 0.2s" }}>
          <SectionRow label="Hero Film" enabled={filmEnabled}
            onEnable={() => { setFilmEnabled(true); setFilms([{ id: 1, seconds: MIN_FILM_SECONDS }]); }}
            onRemove={() => { setFilmEnabled(false); setFilms([{ id: 1, seconds: MIN_FILM_SECONDS }]); }}
            addLabel="+ Add film" />
          {!filmEnabled
            ? <p style={{ fontSize: "13px", color: "#444", lineHeight: 1.6 }}>Campaign-ready editorial film. From 20s — starts at $500 each.</p>
            : <div>
                {films.map((film, idx) => {
                  const c = FILM_BASE_PRICE + Math.max(0, film.seconds - MIN_FILM_SECONDS) * PRICE_PER_FILM_SECOND;
                  return (
                    <div key={film.id} style={{ marginBottom: idx < films.length - 1 ? "20px" : "0", paddingBottom: idx < films.length - 1 ? "20px" : "0", borderBottom: idx < films.length - 1 ? "1px solid #1a1a1a" : "none" }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "10px" }}>
                        <span style={{ fontSize: "11px", letterSpacing: "0.1em", textTransform: "uppercase", color: "#666" }}>Film {idx + 1}</span>
                        {films.length > 1 && (
                          <button onClick={() => removeFilm(film.id)} style={{ fontSize: "11px", color: "#555", background: "transparent", border: "1px solid #252525", borderRadius: "100px", padding: "3px 10px", cursor: "pointer", fontFamily: "inherit", letterSpacing: "0.05em" }}>Remove</button>
                        )}
                      </div>
                      <RangeSlider value={film.seconds} min={MIN_FILM_SECONDS} max={90} step={5} onChange={(v) => updateFilm(film.id, v)} unit="s" />
                      <CostLine>${FILM_BASE_PRICE} base{film.seconds > MIN_FILM_SECONDS ? ` + ${film.seconds - MIN_FILM_SECONDS}s × $${PRICE_PER_FILM_SECOND}` : ""} = <strong style={{ color: "#aaa" }}>${c.toLocaleString()}</strong></CostLine>
                    </div>
                  );
                })}
                <button onClick={addFilm} style={{ marginTop: "16px", padding: "8px 18px", borderRadius: "100px", border: "1px solid #252525", background: "transparent", color: "#666", fontSize: "11px", letterSpacing: "0.06em", textTransform: "uppercase", cursor: "pointer", fontFamily: "inherit", fontWeight: 500, transition: "all 0.2s" }}>
                  + Add another film
                </button>
                {films.length > 1 && (
                  <CostLine style={{ marginTop: "10px" }}>{films.length} films total = <strong style={{ color: "#aaa" }}>${filmCost.toLocaleString()}</strong></CostLine>
                )}
              </div>
          }
        </div>

        {/* ── OUTPUT FORMATS ── */}
        <div style={{ marginBottom: "40px" }}>
          <div style={{ marginBottom: "6px" }}>
            <span style={{ fontSize: "11px", letterSpacing: "0.14em", textTransform: "uppercase", color: "#ccc", fontWeight: 600 }}>Output Formats</span>
          </div>
          <p style={{ fontSize: "13px", color: "#444", marginBottom: "16px", lineHeight: 1.6 }}>
            First format included — each additional <span style={{ fontFamily: "'DM Mono', monospace", color: "#666" }}>+$350</span>.
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
            {ALL_FORMATS.map(f => {
              const active = selectedFormats.includes(f.id);
              const isFree = active && selectedFormats[0] === f.id;
              return (
                <button key={f.id} className="fmt-pill" onClick={() => toggleFormat(f.id)} style={{
                  padding: "9px 18px", borderRadius: "100px",
                  border: `1px solid ${active ? "#fff" : "#1e1e1e"}`,
                  background: active ? "#fff" : "transparent",
                  color: active ? "#000" : "#666",
                  fontSize: "12px", fontWeight: active ? 700 : 400,
                  cursor: "pointer", fontFamily: "inherit", transition: "all 0.15s",
                  display: "flex", alignItems: "center", gap: "5px"
                }}>
                  {f.label}
                  {isFree && <span style={{ fontSize: "9px", opacity: 0.5, fontWeight: 400 }}>free</span>}
                  {active && !isFree && <span style={{ fontSize: "9px", opacity: 0.5, fontWeight: 400 }}>+$350</span>}
                </button>
              );
            })}
          </div>
          {extraFormats > 0 && (
            <div style={{ fontFamily: "'DM Mono', monospace", fontSize: "11px", color: "#555", marginTop: "10px" }}>
              {filmCount > 1
                ? `${extraFormats} extra format${extraFormats > 1 ? "s" : ""} × $${FORMAT_EXTRA_PRICE} × ${filmCount} films = +$${formatCost.toLocaleString()}`
                : `${extraFormats} extra format${extraFormats > 1 ? "s" : ""} × $${FORMAT_EXTRA_PRICE} = +$${formatCost.toLocaleString()}`
              }
            </div>
          )}
        </div>

        {DIVIDER}

        {/* Package tags */}
        {!nothingSelected && (
          <div style={{ marginBottom: "32px" }}>
            <div style={{ fontSize: "10px", letterSpacing: "0.14em", textTransform: "uppercase", color: "#444", marginBottom: "12px", fontWeight: 600 }}>Package includes</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "7px" }}>
              {imagesEnabled && <span style={{ fontSize: "11px", color: "#777", border: "1px solid #1e1e1e", borderRadius: "100px", padding: "4px 12px" }}>{images} editorial images</span>}
              {videosEnabled && <span style={{ fontSize: "11px", color: "#777", border: "1px solid #1e1e1e", borderRadius: "100px", padding: "4px 12px" }}>{videos} short loops (8–10s)</span>}
              {filmEnabled && films.map((f, i) => (
                <span key={f.id} style={{ fontSize: "11px", color: "#777", border: "1px solid #1e1e1e", borderRadius: "100px", padding: "4px 12px" }}>Film {i + 1} ({f.seconds}s)</span>
              ))}
              <span style={{ fontSize: "11px", color: "#777", border: "1px solid #1e1e1e", borderRadius: "100px", padding: "4px 12px" }}>{selectedFormats.length} output format{selectedFormats.length > 1 ? "s" : ""}</span>
              <span style={{ fontSize: "11px", color: "#777", border: "1px solid #1e1e1e", borderRadius: "100px", padding: "4px 12px" }}>Art direction</span>
              <span style={{ fontSize: "11px", color: "#777", border: "1px solid #1e1e1e", borderRadius: "100px", padding: "4px 12px" }}>Structured refinement</span>
            </div>
          </div>
        )}

        {/* ── PRICE BLOCK ── */}
        <div style={{ padding: "28px", borderRadius: "16px", border: `1px solid ${belowMinimum ? "#3d1a1a" : "#1a1a1a"}`, background: "#1c1c1c", transition: "border-color 0.3s", marginBottom: "0" }}>

          {/* Live badge */}
          <div style={{ display: "inline-flex", alignItems: "center", gap: "6px", padding: "5px 14px", border: "1px solid #2a2a2a", borderRadius: "100px", background: "#fff", marginBottom: "20px" }}>
            <div style={{ width: "6px", height: "6px", borderRadius: "50%", background: "#1a1a1a" }} />
            <span style={{ fontSize: "11px", color: "#000", fontWeight: 700, letterSpacing: "0.04em" }}>Live Estimate</span>
          </div>

          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: "4px" }}>
            <div style={{ fontSize: "11px", letterSpacing: "0.12em", textTransform: "uppercase", color: "#444", fontWeight: 600 }}>
              {nothingSelected ? "Select your deliverables above" : tier}
            </div>
            {monthly && !nothingSelected && (
              <span style={{ fontSize: "10px", letterSpacing: "0.06em", textTransform: "uppercase", color: "#888", border: "1px solid #252525", padding: "3px 8px", borderRadius: "100px" }}>20% off</span>
            )}
          </div>

          <div style={{ fontSize: "clamp(44px, 11vw, 60px)", fontWeight: 800, color: nothingSelected ? "#1e1e1e" : "#fff", letterSpacing: "-0.03em", lineHeight: 1, marginBottom: "8px", transition: "color 0.3s" }}>
            {nothingSelected ? "$—" : <AnimatedPrice value={total} />}
          </div>

          {monthly && !nothingSelected && (
            <div style={{ fontSize: "12px", color: "#555", marginBottom: "4px" }}>
              <span style={{ textDecoration: "line-through", color: "#333" }}>${subtotal.toLocaleString()}</span> / month
            </div>
          )}

          {/* Detail rows */}
          <div style={{ margin: "20px 0" }}>
            {[
              { k: "Deliverables", v: nothingSelected ? "—" : `${(imagesEnabled ? images : 0) + (videosEnabled ? videos : 0) + (filmEnabled ? films.length : 0)} assets` },
              { k: "Formats", v: `${selectedFormats.length} selected` },
              { k: "Timeline", v: nothingSelected ? "—" : delivery },
            ].map(({ k, v }) => (
              <div key={k} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: "1px solid #1e1e1e" }}>
                <span style={{ fontSize: "12px", color: "#555" }}>{k}</span>
                <span style={{ fontSize: "12px", color: "#888" }}>{v}</span>
              </div>
            ))}
          </div>

          {belowMinimum && (
            <div style={{ display: "flex", alignItems: "flex-start", gap: "10px", padding: "12px 14px", borderRadius: "8px", background: "rgba(200,50,50,0.07)", border: "1px solid rgba(200,50,50,0.15)", marginBottom: "16px" }}>
              <span style={{ color: "#c84040", fontSize: "13px" }}>!</span>
              <span style={{ fontSize: "12px", color: "#c84040", lineHeight: 1.5 }}>Minimum order is <strong>${MINIMUM_ORDER}</strong>. Add more deliverables to proceed.</span>
            </div>
          )}

          <button
            onClick={() => canProceed && setShowForm(true)}
            style={{
              width: "100%", padding: "15px 24px",
              background: canProceed ? "#fff" : "#141414",
              color: canProceed ? "#000" : "#333",
              border: `1px solid ${canProceed ? "transparent" : "#1e1e1e"}`,
              borderRadius: "100px", fontSize: "14px", fontWeight: 700,
              cursor: canProceed ? "pointer" : "not-allowed",
              fontFamily: "inherit", transition: "all 0.2s",
              display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", letterSpacing: "0.01em",
              marginTop: belowMinimum ? "0" : "4px"
            }}
            onMouseEnter={e => { if (canProceed) e.currentTarget.style.opacity = "0.88"; }}
            onMouseLeave={e => { e.currentTarget.style.opacity = "1"; }}
          >
            Get Started <span style={{ fontSize: "16px" }}>→</span>
          </button>
        </div>

        <p style={{ fontSize: "11px", color: "#2a2a2a", textAlign: "center", marginTop: "16px", lineHeight: 1.6 }}>
          All orders include brand art direction, structured refinement, and platform-ready formats.
        </p>

        {/* ── INTAKE FORM ── */}
        {showForm && (
          <div ref={formRef} style={{ marginTop: "60px" }}>
            {DIVIDER}

            {/* Order recap */}
            <div style={{ padding: "20px 24px", border: "1px solid #1a1a1a", borderRadius: "14px", background: "#1c1c1c", marginBottom: "36px" }}>
              <div style={{ fontSize: "10px", letterSpacing: "0.14em", textTransform: "uppercase", color: "#444", marginBottom: "10px", fontWeight: 600 }}>Your order</div>
              <div style={{ fontSize: "34px", fontWeight: 800, color: "#fff", letterSpacing: "-0.02em", marginBottom: "12px" }}>
                ${total.toLocaleString()}{monthly && <span style={{ fontSize: "15px", color: "#555", fontWeight: 400 }}> /mo</span>}
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                {imagesEnabled && <span style={{ fontSize: "11px", color: "#666", border: "1px solid #222", borderRadius: "100px", padding: "4px 12px" }}>{images} images</span>}
                {videosEnabled && <span style={{ fontSize: "11px", color: "#666", border: "1px solid #222", borderRadius: "100px", padding: "4px 12px" }}>{videos} videos</span>}
                {filmEnabled && films.map((f, i) => (
                  <span key={f.id} style={{ fontSize: "11px", color: "#666", border: "1px solid #222", borderRadius: "100px", padding: "4px 12px" }}>Film {i + 1} ({f.seconds}s)</span>
                ))}
                <span style={{ fontSize: "11px", color: "#666", border: "1px solid #222", borderRadius: "100px", padding: "4px 12px" }}>{selectedFormats.length} format{selectedFormats.length > 1 ? "s" : ""}</span>
              </div>
            </div>

            <h2 style={{ fontSize: "28px", fontWeight: 800, color: "#fff", letterSpacing: "-0.02em", marginBottom: "6px" }}>Your details.</h2>
            <p style={{ fontSize: "13px", color: "#555", lineHeight: 1.6, marginBottom: "28px" }}>Tell us about you and your campaign. We'll follow up within 2 business days.</p>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
              <InputField label="First name" required error={errors.firstName}>
                <input type="text" value={form.firstName} onChange={e => setField("firstName", e.target.value)} placeholder="Elon" style={{ borderColor: errors.firstName ? "#c84040" : "#1e1e1e" }} />
              </InputField>
              <InputField label="Last name" required error={errors.lastName}>
                <input type="text" value={form.lastName} onChange={e => setField("lastName", e.target.value)} placeholder="Musk" style={{ borderColor: errors.lastName ? "#c84040" : "#1e1e1e" }} />
              </InputField>
            </div>

            <InputField label="Company name">
              <input type="text" value={form.company} onChange={e => setField("company", e.target.value)} placeholder="SpaceX" />
            </InputField>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
              <InputField label="Email" required error={errors.email}>
                <input type="email" value={form.email} onChange={e => setField("email", e.target.value)} placeholder="hello@brand.com" style={{ borderColor: errors.email ? "#c84040" : "#1e1e1e" }} />
              </InputField>
              <InputField label="Phone number">
                <input type="tel" value={form.phone} onChange={e => setField("phone", e.target.value)} placeholder="+61 4xx xxx xxx" />
              </InputField>
            </div>

            <InputField label="Website">
              <input type="url" value={form.website} onChange={e => setField("website", e.target.value)} placeholder="https://yourbrand.com" />
            </InputField>

            <InputField label="Core campaign objective" required error={errors.objective}>
              <textarea rows={3} value={form.objective} onChange={e => setField("objective", e.target.value)} placeholder="Product launch, ad creatives, website visuals, brand campaign..." style={{ resize: "vertical", lineHeight: 1.55, borderColor: errors.objective ? "#c84040" : "#1e1e1e" }} />
            </InputField>

            <InputField label="Additional information">
              <textarea rows={3} value={form.additionalInfo} onChange={e => setField("additionalInfo", e.target.value)} placeholder="Feel free to share anything else that could help us prepare for your project..." style={{ resize: "vertical", lineHeight: 1.55 }} />
            </InputField>

            <InputField label="Where did you hear about us?" required error={errors.hearFrom}>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginTop: "2px" }}>
                {HEAR_OPTIONS.map(opt => (
                  <button key={opt} className="hear-pill" onClick={() => setField("hearFrom", opt)} style={{
                    padding: "8px 18px", borderRadius: "100px",
                    border: `1px solid ${form.hearFrom === opt ? "#fff" : "#1e1e1e"}`,
                    background: form.hearFrom === opt ? "#fff" : "transparent",
                    color: form.hearFrom === opt ? "#000" : "#555",
                    fontSize: "13px", fontWeight: form.hearFrom === opt ? 700 : 400,
                    cursor: "pointer", fontFamily: "inherit", transition: "all 0.15s"
                  }}>{opt}</button>
                ))}
              </div>
            </InputField>

            {submitError && (
              <div style={{ padding: "12px 14px", background: "rgba(200,50,50,0.07)", border: "1px solid rgba(200,50,50,0.15)", borderRadius: "8px", fontSize: "12px", color: "#c84040", marginBottom: "16px" }}>{submitError}</div>
            )}

            <div style={{ display: "flex", gap: "10px", marginTop: "8px" }}>
              <button onClick={() => setShowForm(false)} style={{ padding: "14px 20px", background: "transparent", color: "#555", border: "1px solid #1e1e1e", borderRadius: "100px", fontSize: "13px", cursor: "pointer", fontFamily: "inherit" }}>← Back</button>
              <button onClick={handleSubmit} disabled={submitting} style={{ flex: 1, padding: "14px 24px", background: submitting ? "#141414" : "#fff", color: submitting ? "#444" : "#000", border: "none", borderRadius: "100px", fontSize: "14px", fontWeight: 700, cursor: submitting ? "not-allowed" : "pointer", fontFamily: "inherit", transition: "all 0.2s" }}>
                {submitting ? "Sending..." : "Submit Order Request →"}
              </button>
            </div>
            <p style={{ fontSize: "11px", color: "#252525", textAlign: "center", marginTop: "14px" }}>No payment taken at this stage.</p>
          </div>
        )}
      </div>
    </div>
  );
}
